import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ProteinAlignmentKind, ProteinAlignment} from 'constants/AlignmentConstants';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {AaSequenceAlignmentState} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {isFullAaSequenceAlignment} from 'molbio/AlignmentUtil';

/**
 * The state of the store, `alignmentStatesById`, contains a dictionary of alignment states keyed by
 * alignment ID. Each alignment state contains the following fields:
 *   - `alignment`: a summary alignment dict, or full alignment dict that is loaded when the alignment opened
 *   - `knownLinkedProteinIds`: a set of the IDs of the proteins that we know are linked to the alignment.
 *
 * If we haven't fetched the full alignment dictionary, `knownLinkedProteinIds` will only contain the
 * linked proteins that have already been opened. This is because when we open a protein, we add all of
 * the alignment summary dicts to the store, but we don't know all of the proteins that are linked to
 * an alignment from its summary dict.
 *
 * This store contains the source of truth of which alignments are linked or belong to a given protein,
 * not the protein dicts in the AaSequenceStore.
 */
class AaSequenceAlignmentStore extends Store {
  alignmentStatesById!: {[alignmentId: string]: AaSequenceAlignmentState};
  override resetState(): void {
    this.alignmentStatesById = {};
  }

  getAlignmentStateById(alignmentId: string): AaSequenceAlignmentState | undefined {
    return this.alignmentStatesById[alignmentId];
  }

  _setAlignmentStateById(
    alignmentId: string,
    {alignment, knownLinkedAaSequenceIds}: AaSequenceAlignmentState
  ): void {
    this.alignmentStatesById[alignmentId] = {alignment, knownLinkedAaSequenceIds};
  }

  _deleteAlignmentStateById(alignmentId: string): void {
    delete this.alignmentStatesById[alignmentId];
  }

  // This list may not be complete if an AA sequence has not been opened.
  getAlignments(proteinId: string): Array<ProteinAlignment> {
    // eslint-disable-next-line no-restricted-syntax
    const savedAlignmentStates = _.values(this.alignmentStatesById).filter(
      (alignmentState) => alignmentState.alignment.proteinId === proteinId
    );
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(savedAlignmentStates, 'alignment');
  }

  // This list may not be complete if an AA sequence has not been opened.
  getLinkedAlignments(proteinId: string): Array<ProteinAlignment> {
    // eslint-disable-next-line no-restricted-syntax
    const linkedAlignmentStates = _.values(this.alignmentStatesById).filter((alignmentState) =>
      alignmentState.knownLinkedAaSequenceIds.has(proteinId)
    );
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(linkedAlignmentStates, 'alignment');
  }

  _getUpdatedAlignmentState(alignmentSummaryDict: ProteinAlignment): AaSequenceAlignmentState {
    return (
      this.getAlignmentStateById(alignmentSummaryDict.id) ?? {
        alignment: alignmentSummaryDict,
        knownLinkedAaSequenceIds: new Set(),
      }
    );
  }

  _getUpdatedLinkedAlignmentState(
    proteinId: string,
    alignmentSummaryDict: ProteinAlignment
  ): AaSequenceAlignmentState {
    if (!this.getAlignmentStateById(alignmentSummaryDict.id)) {
      return {
        alignment: alignmentSummaryDict,
        knownLinkedAaSequenceIds: new Set([proteinId]),
      };
    } else {
      return reactUpdate(this.getAlignmentStateById(alignmentSummaryDict.id)!, {
        knownLinkedAaSequenceIds: {$apply: (proteinIds) => proteinIds.add(proteinId)},
      });
    }
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.PROTEIN_OPEN_COMPLETED: {
        const {protein} = action;
        for (const alignment of protein.alignments) {
          this._setAlignmentStateById(alignment.id, this._getUpdatedAlignmentState(alignment));
        }
        for (const alignment of protein.linkedAlignments) {
          this._setAlignmentStateById(
            alignment.id,
            this._getUpdatedLinkedAlignmentState(protein.id, alignment)
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_OPEN_COMPLETED: {
        const {alignment} = action;
        // eslint-disable-next-line no-restricted-syntax
        const linkedAaSequenceIds = _.pluck(alignment.rows.slice(1), 'originalProteinId').filter(
          (proteinId) => proteinId !== undefined
        );
        this._setAlignmentStateById(alignment.id, {
          alignment,
          knownLinkedAaSequenceIds: new Set(linkedAaSequenceIds),
        });
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_DELETE_COMPLETED: {
        const {alignmentId} = action;
        this._deleteAlignmentStateById(alignmentId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_RENAME_COMPLETED: {
        const {alignmentId, name} = action;
        const alignmentState = this.getAlignmentStateById(alignmentId);
        if (alignmentState) {
          let newAlignmentState = reactUpdate(alignmentState, {
            alignment: {
              name: {$set: name},
            },
          });
          if (
            isFullAaSequenceAlignment(alignmentState.alignment) &&
            alignmentState.alignment.alignmentType === ProteinAlignmentKind.CONSENSUS
          ) {
            newAlignmentState = reactUpdate(newAlignmentState, {
              alignment: {
                rows: {0: {name: {$set: name}}},
              },
            });
          }
          this._setAlignmentStateById(alignmentId, newAlignmentState);
          this.emitChange();
        }
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const aaSequenceAlignmentStore = new AaSequenceAlignmentStore(AppDispatcher);
export default aaSequenceAlignmentStore;
